<template>
  <div
    class="component bg-white flex flex-col w-full tablet-h:w-[340px] tablet-h:h-[500px] tablet-h:rounded-md"
    :style="styles"
  >
    <div
      class="flex items-center justify-between px-3 pt-[18px] tablet-h:py-2.5 mb-7 tablet-h:mb-0 tablet-h:border-b tablet-h:border-[#E5E5E5]"
    >
      <div
        class="font-semibold font-primary text-gray-800 text-[23px] tablet-h:text-[14px] leading-[20px] tablet-h:leading-[18px]"
      >
        Notifications
      </div>

      <div
        class="hidden tablet-h:block font-secondary text-[12px] leading-[18px] cursor-pointer text-green-900"
        @click="handleMarkAllAsRead"
      >
        Mark all as read
      </div>

      <Icon
        name="ThreeDots"
        class="tablet-h:hidden stroke-gray-800 fill-gray-800"
        :size="20"
        @click="handleMenuClick"
      />
    </div>

    <div class="h-full overflow-y-auto" ref="container">
      <component
        v-for="notification in result?.notifications.notifications"
        :key="notification.id"
        :data-notification-id="notification.id"
        :is="getComponent(notification as Notification)"
        :notification="notification as Notification"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import FileAccessNotification from '@/shared/components/notifications/FileAccessNotification.vue'
import IdentityVerificationNotification from '@/shared/components/notifications/IdentityVerificationNotification.vue'
import InviteNotification from '@/shared/components/notifications/InviteNotification.vue'
import NewAccountNotification from '@/shared/components/notifications/NewAccountNotification.vue'
import PaymentNotification from '@/shared/components/notifications/PaymentNotification.vue'
import ReceivedFileNotification from '@/shared/components/notifications/ReceivedFileNotification.vue'
import SubscriptionNotification from '@/shared/components/notifications/SubscriptionNotification.vue'
import UploadFailedNotification from '@/shared/components/notifications/UploadFailedNotification.vue'
import {
  NotificationsDocument,
  NotificationType,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  useNotificationsQuery,
  type Notification,
  type NotificationsQuery,
} from '@/shared/graphql/legacy/generated'
import { apolloClient } from '@/shared/graphql/client'
import { useDevice } from '@/shared/composables/useDevice'
import { MOBILE_AUTH_NAVBAR_HEIGHT } from '@/shared/composables/useAvailableHeight'
import { useEventListener } from '@/shared/composables/useEventListener'
import { Icon } from '@dacr/ui'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
// Notifications don't use dialogs

const { result, loading, fetchMore } = useNotificationsQuery()
const { mutate: markNotificationAsRead } = useMarkNotificationAsReadMutation()
const { mutate: markAllAsRead } = useMarkAllNotificationsAsReadMutation()

const device = useDevice()
const container = ref<HTMLElement | null>(null)
const observer = ref<IntersectionObserver>()

const styles = computed(() => ({
  ...((device.isMobile.value || device.isTabletVertical.value) && {
    height: `${device.viewportHeight.value - MOBILE_AUTH_NAVBAR_HEIGHT}px`,
  }),
}))

const readNotificationIds = ref<string[]>([])

const getComponent = (notification: Notification) => {
  if (notification.type === NotificationType.UserCreated) {
    return NewAccountNotification
  }

  if (
    [
      NotificationType.UserVerificationSucess,
      NotificationType.UserVerificationFailed,
    ].includes(notification.type)
  ) {
    return IdentityVerificationNotification
  }

  if (
    [
      NotificationType.AccountPaymentRequiresAction,
      NotificationType.InvoiceOverdue,
    ].includes(notification.type)
  ) {
    return PaymentNotification
  }

  if (notification.type === NotificationType.SubscriptionDowngradeFailed) {
    return SubscriptionNotification
  }

  if (
    [
      NotificationType.FileUploadFailed,
      NotificationType.FileUploadEmptyFile,
    ].includes(notification.type)
  ) {
    return UploadFailedNotification
  }

  if (
    [NotificationType.ReceivedFile, NotificationType.ReceivedLicense].includes(
      notification.type
    )
  ) {
    return ReceivedFileNotification
  }

  if (
    [
      NotificationType.FileOwnerAccessAccepted,
      NotificationType.FileOwnerAccessPending,
      NotificationType.FileAuthorAccessAccepted,
      NotificationType.FileAuthorAccessPending,
      NotificationType.FilePrivacyAccessAccepted,
      NotificationType.TeamMemberAccessPending,
      NotificationType.TeamMemberAccessAccepted,
    ].includes(notification.type)
  ) {
    return InviteNotification
  }

  if (
    [
      NotificationType.FileOwnerAccessRemoved,
      NotificationType.FileAuthorAccessRemoved,
      NotificationType.TeamMemberAccessRemoved,
    ].includes(notification.type)
  ) {
    return FileAccessNotification
  }
}

const observeNotificationsElements = () => {
  Array.from(document.querySelectorAll('[data-notification-id]'))
    .filter((el) => {
      const notification = result.value?.notifications.notifications.find(
        (notification) =>
          notification.id === el.getAttribute('data-notification-id')
      )

      return !notification?.read
    })
    .forEach((el) => observer.value?.observe(el))
}

const fetchNextPage = async () => {
  const nextValue = result.value?.notifications.next

  if (nextValue && !loading.value) {
    await fetchMore({
      variables: {
        next: nextValue,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          __typename: 'Query',
          notifications: {
            __typename: 'NotificationsResponse',
            notifications: [
              ...previousData.notifications.notifications,
              ...newData.notifications.notifications,
            ],
            next: newData.notifications.next,
            unreadNotifications: previousData.notifications.unreadNotifications,
          },
        }
      },
    })

    observeNotificationsElements()
  }
}

const handleScroll = () => {
  const containerHeight = container.value?.scrollHeight || 0
  const scrollPos =
    (container.value?.clientHeight || 0) + (container.value?.scrollTop || 0)

  if (scrollPos / containerHeight >= 0.5) {
    fetchNextPage()
  }
}

useEventListener(container, 'scroll', handleScroll)

const updateReadNotificationsInCache = (all?: boolean) => {
  apolloClient.cache.updateQuery<NotificationsQuery>(
    {
      query: NotificationsDocument,
    },
    (response) => {
      if (!response) return

      const newNotifications = response?.notifications.notifications.map(
        (notification) => {
          if (all || readNotificationIds.value.includes(notification.id)) {
            return {
              ...notification,
              read: true,
            }
          }

          return notification
        }
      )

      const newUnreadNotificationsCount = Math.max(
        0,
        (response?.notifications.unreadNotifications || 0) -
          readNotificationIds.value.length
      )

      return {
        ...response,
        notifications: {
          ...response?.notifications,
          notifications: newNotifications,
          unreadNotifications: all ? 0 : newUnreadNotificationsCount,
        },
      }
    }
  )

  readNotificationIds.value = []
}

const removeNotificationBadge = () => {
  apolloClient.cache.updateQuery<NotificationsQuery>(
    {
      query: NotificationsDocument,
    },
    (response) => {
      if (!response) return

      return {
        ...response,
        notifications: {
          ...response.notifications,
          unreadNotifications: 0,
        },
      }
    }
  )
}

const handleMarkAllAsRead = () => {
  markAllAsRead()
  updateReadNotificationsInCache(true)
}

const { open } = useDialog()

const handleMenuClick = () => {
  open(Dialogs.NotificationMenu)
}

onMounted(() => {
  observer.value = new IntersectionObserver(
    (entries) => {
      const ids: string[] = []
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        const notificationId = entry.target.getAttribute('data-notification-id')

        if (!notificationId) return

        ids.push(notificationId)

        entry.target.removeAttribute('data-notification-id')
        observer.value?.unobserve(entry.target)
      })

      if (ids.length) {
        markNotificationAsRead({
          ids,
        })

        readNotificationIds.value = readNotificationIds.value.concat(ids)

        if (
          readNotificationIds.value.length ===
          result.value?.notifications.unreadNotifications
        ) {
          removeNotificationBadge()
        }
      }
    },
    {
      root: container.value,
      threshold: 0.5,
    }
  )

  observeNotificationsElements()
})

onBeforeUnmount(() => {
  observer.value?.disconnect()
  updateReadNotificationsInCache()
})
</script>

<style scoped>
@media (min-width: 1023px) {
  .component {
    box-shadow:
      0 0 8px rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.2);
  }
}
</style>
